import {
  getCanonicalLocales,
  getOption,
  resolveLocale,
  invariant,
  SupportedLocales,
  partitionPattern,
  isLiteralPart,
  decode256
} from './utils.js'
import Locale from './Locale.js'
import lists from './data/lists.js'
import SlotMap from '@neumatter/slot-map'
import BVON from '@neumatter/bvon'

/**
 * https://tc39.es/proposal-intl-list-format/#sec-createstringlistfromiterable
 * @param list list
 */
function stringListFromIterable (list) {
  if (list === undefined) {
    return []
  }
  const result = []
  for (const el of list) {
    if (typeof el !== 'string') {
      throw new TypeError(`array list[${list.indexOf(el)}] is not type String`)
    }
    result.push(el)
  }
  return result
}

function createPartsFromList (internalSlotMap, list) {
  const size = list.length
  if (size === 0) {
    return []
  }
  if (size === 2) {
    const pattern = internalSlotMap.templatePair
    const first = { type: 'element', value: list[0] }
    const second = { type: 'element', value: list[1] }
    return deconstructPattern(pattern, { 0: first, 1: second })
  }
  const last = {
    type: 'element',
    value: list[size - 1]
  }
  let parts = last
  let i = size - 2
  while (i >= 0) {
    let pattern
    if (i === 0) {
      pattern = internalSlotMap.templateStart
    } else if (i < size - 2) {
      pattern = internalSlotMap.templateMiddle
    } else {
      pattern = internalSlotMap.templateEnd
    }
    const head = { type: 'element', value: list[i] }
    parts = deconstructPattern(pattern, { 0: head, 1: parts })
    i--
  }
  return parts
}

function deconstructPattern (pattern, placeables) {
  const patternParts = partitionPattern(pattern)
  const result = []
  for (const patternPart of patternParts) {
    const { type: part } = patternPart
    if (isLiteralPart(patternPart)) {
      result.push({
        type: 'literal',
        value: patternPart.value
      })
    } else {
      invariant(part in placeables, `${part} is missing from placables`)
      const subst = placeables[part]
      if (Array.isArray(subst)) {
        result.push(...subst)
      } else {
        result.push(subst)
      }
    }
  }
  return result
}

const DEFAULT_LOCALE = new Intl.DateTimeFormat().resolvedOptions().locale

export default class ListFormat {
  #internal = {}

  constructor (locales, options) {
    this.#internal.initializedListFormat = true
    const requestedLocales = getCanonicalLocales(locales)
    const opt = Object.create(null)
    const opts = options || {}
    const matcher = getOption(
      opts,
      'localeMatcher',
      'string',
      ['best fit', 'lookup'],
      'best fit'
    )
    opt.localeMatcher = matcher
    const [localeData, availableLocales] = getLocaleDataLazy()
    const r = resolveLocale(
      availableLocales,
      requestedLocales,
      opt,
      ListFormat.relevantExtensionKeys,
      localeData,
      ListFormat.getDefaultLocale
    )

    this.#internal.locale = r.locale

    const type = getOption(
      opts,
      'type',
      'string',
      ['conjunction', 'disjunction', 'unit'],
      'conjunction'
    )
    this.#internal.type = type
    const style = getOption(
      opts,
      'style',
      'string',
      ['long', 'short', 'narrow'],
      'long'
    )
    this.#internal.style = style
    const { dataLocale } = r
    const dataLocaleData = localeData[dataLocale]
    invariant(!!dataLocaleData, `Missing locale data for ${dataLocale}`)
    const dataLocaleTypes = dataLocaleData[type]
    const templates = dataLocaleTypes ? dataLocaleTypes[style] : undefined
    this.#internal.templatePair = templates.pair
    this.#internal.templateStart = templates.start
    this.#internal.templateMiddle = templates.middle
    this.#internal.templateEnd = templates.end
    SlotMap.setInternal(this, 'internal', this.#internal)
  }

  format (elements) {
    let result = ''
    const parts = createPartsFromList(
      this.#internal,
      stringListFromIterable(elements)
    )
    if (!Array.isArray(parts)) {
      return parts.value
    }
    for (const p of parts) {
      result += p.value
    }
    return result
  }

  formatToParts (elements) {
    const parts = createPartsFromList(
      this.#internal,
      stringListFromIterable(elements)
    )
    if (!Array.isArray(parts)) {
      return [parts]
    }
    const result = []
    for (const part of parts) {
      result.push({ ...part })
    }
    return result
  }

  resolvedOptions () {
    return {
      locale: this.#internal.locale,
      type: this.#internal.type,
      style: this.#internal.style
    }
  }

  static supportedLocalesOf (locales, options) {
    return SupportedLocales(
      getLocaleDataLazy()[1],
      getCanonicalLocales(locales),
      options
    )
  }

  static getDefaultLocale () {
    return DEFAULT_LOCALE
  }

  static relevantExtensionKeys = []
  static polyfilled = true
}

function getLocaleDataLazy () {
  let al = SlotMap.getInternal(ListFormat, 'availableLocales')
  let ld = SlotMap.getInternal(ListFormat, 'localeData')

  if (ld === undefined || al === undefined) {
    const view = decode256(lists)
    const data = BVON.deserialize(view)
    const localeData = {}
    const availableLocales = new Set()

    for (const locale in data) {
      const minimizedLocale = new Locale(locale).minimize().toString()
      localeData[locale] = data[minimizedLocale]
      availableLocales.add(minimizedLocale)
      availableLocales.add(locale)
    }

    SlotMap.setInternal(ListFormat, 'availableLocales', availableLocales)
    SlotMap.setInternal(ListFormat, 'localeData', localeData)
    al = availableLocales
    ld = localeData
  }

  return [ld, al]
}

SlotMap.setInternal(ListFormat, 'createPartsFromList', createPartsFromList)
